import {
  IonActionSheet,
  IonCard,
  IonCardContent,
  IonFab,
  IonFabButton,
  IonIcon,
  IonItem,
  IonLabel,
  IonList,
  IonLoading,
  useIonAlert,
  useIonToast,
} from "@ionic/react";
import { add } from "ionicons/icons";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { getExistingCodes, submitCodes } from "../api/AnalysisCodes";
import {
  AnalysisCode,
  CompletionFieldDef,
  ValidValue,
} from "../models/analysiscodes/AnalysisCodes";
import { decodeParam } from "../util/ApiHelper";
import "./AnalysisCodes.css";
import AnalysisCodesModal from "./AnalysisCodesModal";
import { ApiResponse } from "../models/api/ApiResponse";
import {
  TranslationMessagesContext,
  TranslationsContext,
} from "../util/Translations";
import ApiError from "./ApiError";
import { reactPlugin } from "../util/AppInsights";
import { withAITracking } from "@microsoft/applicationinsights-react-js";
import useGetAnalysisCodes from "../hooks/work-order/useGetAnalysisCodes";
import { useWorkOrderStore } from "../store/wo-order";
import { useShallow } from "zustand/react/shallow";
import useSaveAnalysisCodes from "../hooks/work-order/useSaveAnalysisCodes";
import { useOfflineStore } from "../store/offline-mode";
import { useWoOfflineUpdatesStore } from "../store/wo-offline-updates";

const AnalysisCodes: React.FC = () => {
  const { translations } = useContext(TranslationsContext);
  const { translatedMessages } = useContext(TranslationMessagesContext);
  const { workOrderId } = useParams<{ workOrderId: string }>();
  const displayWo = decodeParam(workOrderId);
  const [present] = useIonToast();
  const [analysisCodeForEdit, setAnalysisCodeForEdit] =
    useState<AnalysisCode>();
  const [selectedCode, setSelectedCode] = useState<AnalysisCode>();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [showLoading, setShowLoading] = useState(false);
  const [errorData, setErrorData] = useState("");
  const [actionSheetVisible, setActionSheetVisible] = useState(false);
  const [presentAlert] = useIonAlert();

  const { getAnalysisCodes } = useGetAnalysisCodes();
  const { saveAnalysisCodes } = useSaveAnalysisCodes();
  const analysisCodes = useWorkOrderStore(
    useShallow((state) => state.getAnalysisCodesForWo(displayWo))
  );
  const workOrder = useWorkOrderStore(
    useShallow((state) => state.getWorkOrderById(displayWo))
  );
  const ccaFieldDefinitions = useWorkOrderStore(
    useShallow((state) => state.getCCAFieldDefinitionsForWo(displayWo))
  );
  const completeColumnDetails = useWorkOrderStore(
    useShallow((state) => state.getCompleteColumnDetailsForWo(displayWo))
  );

  const setAnalysisCodes = useWorkOrderStore(
    (state) => state.setWoAnalysisCodes
  );

  const isOffline = useOfflineStore((state) => state.isOffline);
  const addAnalysisCodesToCreateUpdate = useWoOfflineUpdatesStore(
    (state) => state.addAnalysisCodesToCreateUpdate
  );

  const componentValues: ValidValue[] = completeColumnDetails
    ? completeColumnDetails.component
    : ccaFieldDefinitions?.componentField?.ValidValues;
  const conditionValues: ValidValue[] = completeColumnDetails
    ? completeColumnDetails.condition
    : ccaFieldDefinitions?.conditionField?.ValidValues;
  const actionValues: ValidValue[] = completeColumnDetails
    ? completeColumnDetails.action
    : ccaFieldDefinitions?.actionField?.ValidValues;

  const componentField: CompletionFieldDef =
    ccaFieldDefinitions?.componentField;
  const conditionField: CompletionFieldDef =
    ccaFieldDefinitions?.conditionField;
  const actionField: CompletionFieldDef = ccaFieldDefinitions?.actionField;

  const workOrderEvents = workOrder?.Events;

  const setup = useCallback(async () => {
    if (analysisCodes) return; //got it from the cache
    setShowLoading(true);
    const response = await getAnalysisCodes(displayWo);
    if (response?.isError) setErrorData(response.data);
    else setErrorData("");
    setShowLoading(false);
  }, [analysisCodes, displayWo, getAnalysisCodes]);

  useEffect(() => {
    setup();
  }, [setup]);

  const getCodes = async () => {
    const dateCreated = workOrderEvents?.DateCreated;
    const timeCreated = workOrderEvents?.TimeCreated;
    if (dateCreated && timeCreated) {
      setShowLoading(true);
      const existingCodes = await getExistingCodes(dateCreated, timeCreated);
      setAnalysisCodes(displayWo, existingCodes || []);
      setShowLoading(false);
    }
  };

  const onAdd = () => {
    setAnalysisCodeForEdit(undefined);
    setIsModalOpen(true);
  };

  const onEdit = (code?: AnalysisCode) => {
    if (!code) {
      return;
    }
    setAnalysisCodeForEdit(code);
    setIsModalOpen(true);
  };

  const onCancel = () => {
    setIsModalOpen(false);
    setAnalysisCodeForEdit(undefined);
  };

  const onDelete = async (codeToDelete?: AnalysisCode) => {
    if (!codeToDelete) {
      return;
    }
    const newArray = (analysisCodes ?? []).filter((code) => {
      return (
        code.Component !== codeToDelete.Component ||
        code.Condition !== codeToDelete.Condition ||
        code.Action !== codeToDelete.Action
      );
    });
    await submitCodes(newArray, workOrderEvents);
    await getCodes();
  };

  const onSave = async (analysisCode?: AnalysisCode) => {
    if (!analysisCode) {
      return;
    }
    let payload: AnalysisCode[];
    if (!analysisCodeForEdit) {
      payload = [
        ...(analysisCodes ?? []),
        {
          ...analysisCode,
          TimeCreated: workOrderEvents?.TimeCreated,
          DateCreated: workOrderEvents?.DateCreated,
          Plant: workOrderEvents?.Plant,
        },
      ];
    } else {
      payload = (analysisCodes ?? []).map((code) => {
        if (
          code.Component === analysisCodeForEdit?.Component &&
          code.Condition === analysisCodeForEdit?.Condition &&
          code.Action === analysisCodeForEdit?.Action
        ) {
          return analysisCode;
        }
        return code;
      });
    }

    if (isOffline) {
      addAnalysisCodesToCreateUpdate({
        codes: payload,
        workOrderEvents,
        woNumber: displayWo,
      });
      setIsModalOpen(false);
      presentAlert({
        message:
          translatedMessages["MobOfflineCCA"]?.MessageText ||
          "Analysis code added in offline store.",
        buttons: [
          {
            text: translations["lbl_btn_ok"] || "OK",
          },
        ],
      });
      return; //we will sync once use backs online
    }

    setShowLoading(true);
    const response = await saveAnalysisCodes(payload, workOrderEvents);
    setShowLoading(false);
    if (!response.data.Success) {
      handleApiFailResponse(response);
    } else {
      await getCodes();
      setIsModalOpen(false);
      setAnalysisCodeForEdit(undefined);
    }
  };

  const handleApiFailResponse = (response: ApiResponse) => {
    if (!response.data?.Success) {
      const strings: string[] = response.data?.MessageList?.map(
        (message: { Text: string }) => {
          return message.Text;
        }
      );
      present({ message: strings.join(" "), color: "danger", duration: 5000 });
    }
  };

  const getValidValueDetails = (codes: ValidValue[], code: string) => {
    return codes.find((value) => {
      return value.Code === code;
    });
  };
  const getDescription = (
    code: string,
    field: "component" | "condition" | "action"
  ) => {
    let codes: ValidValue[];
    switch (field) {
      case "component":
        codes = componentValues;
        break;
      case "condition":
        codes = conditionValues;
        break;
      case "action":
        codes = actionValues;
        break;
      default:
        codes = [];
        break;
    }
    const validValue = getValidValueDetails(codes, code);
    if (validValue?.Description) {
      return ` - ${validValue?.Description}`;
    }
    return "";
  };

  return (
    <>
      <ApiError errorData={errorData} />
      <IonLoading
        isOpen={showLoading}
        onDidDismiss={() => setShowLoading(false)}
      />
      <IonFab
        vertical="bottom"
        horizontal="end"
        style={{
          position: "fixed",
          bottom: "calc(16px + env(safe-area-inset-bottom))",
          right: "16px",
          zIndex: 1000,
        }}
      >
        <IonFabButton onClick={onAdd}>
          <IonIcon icon={add} />
        </IonFabButton>
      </IonFab>

      {(analysisCodes ?? []).map((code, index) => {
        return (
          <IonCard
            key={`${displayWo}-analysis-code-${index}`}
            onClick={() => {
              setSelectedCode(code);
              setActionSheetVisible(true);
            }}
          >
            <IonCardContent class="analysis-code-card-content">
              <IonList lines="full">
                {code.Component && (
                  <IonItem>
                    <IonLabel>
                      <p>{componentField?.TranslatedIdText || "Component"}</p>
                      <h6>
                        {code.Component}
                        {getDescription(code.Component, "component")}
                      </h6>
                    </IonLabel>
                  </IonItem>
                )}
                {code.Condition && (
                  <IonItem>
                    <IonLabel>
                      <p>{conditionField?.TranslatedIdText || "Condition"}</p>
                      <h6>
                        {code.Condition}
                        {getDescription(code.Condition, "condition")}
                      </h6>
                    </IonLabel>
                  </IonItem>
                )}
                {code.Action && (
                  <IonItem>
                    <IonLabel>
                      <p>{actionField?.TranslatedIdText || "Action"}</p>
                      <h6>
                        {code.Action}
                        {getDescription(code.Action, "action")}
                      </h6>
                    </IonLabel>
                  </IonItem>
                )}
              </IonList>
            </IonCardContent>
          </IonCard>
        );
      })}
      <IonActionSheet
        isOpen={actionSheetVisible}
        onDidDismiss={() => {
          setSelectedCode(undefined);
          setActionSheetVisible(false);
        }}
        buttons={[
          {
            text: translations["lbl_btn_edit"] || "Edit",
            handler: () => {
              onEdit(selectedCode);
            },
          },
          {
            text: translations["lbl_btn_delete"] || "Delete",
            role: "destructive",
            handler: () => {
              presentAlert({
                message:
                  translatedMessages["ConfirmDelete"]?.MessageText.replace(
                    "@1",
                    selectedCode?.Component
                  ) ||
                  `Are you sure you want to delete ${selectedCode?.Component}?`,
                buttons: [
                  {
                    text: translations["lbl_btn_cancel"] || "Cancel",
                    role: "cancel",
                  },
                  {
                    text: translations["lbl_btn_delete"] || "Delete",
                    role: "destructive",
                    handler: () => {
                      onDelete(selectedCode);
                    },
                  },
                ],
              });
            },
          },
          {
            text: translations["lbl_btn_cancel"] || "Cancel",
            role: "cancel",
          },
        ]}
      ></IonActionSheet>
      <AnalysisCodesModal
        isOpen={isModalOpen}
        analysisCodeForEdit={analysisCodeForEdit}
        analysisCodes={analysisCodes ?? []}
        onCancel={onCancel}
        onSave={onSave}
      />
    </>
  );
};

export default withAITracking(reactPlugin, AnalysisCodes, "AnalysisCodes");
